import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Why every entrepreneur should take up boxing",
  "description": "",
  "published": "2012-02-10T00:00:00.000Z",
  "redirect_from": ["/blog/why-all-entrepreneurs-should-take-up-boxing/swizec/3646"],
  "categories": "Battlestar Galactica, Boxing, Business, Ferdie Pacheco, Freelancing, Joe Frazier, Muhammad Ali, Opinions, Paul Graham, Personal, Punch (combat), Side Projects, Thrilla in Manila",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Punch_%28combat%29",
        "title": "Punch (combat)"
      }}>{`punch`}</a>{` to the face.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/Muhammad-Ali-Wallpaper-300x225.jpg",
        "alt": "Victory",
        "title": "Victory"
      }}></img></p>
    <p>{`That's what it feels like when you release something and nobody likes it.`}</p>
    <p>{`A hard punch to the face.`}</p>
    <p>{`That's what it feels like when you miss a block in `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Boxing",
        "title": "Boxing"
      }}>{`boxing`}</a>{`.`}</p>
    <p>{`Launching products and boxing have so much in common they are pretty much the same thing. Both are about overcoming immense adversity through sheer skill, tactic and strategy. Both are simple and yet the hardest thing you have ever done. Ever.`}</p>
    <h2 {...{
      "id": "boxing"
    }}>{`Boxing`}</h2>
    <p>{`Punch the other guy more than he punches you.`}</p>
    <p>{`At your disposal are three simple attacking tools - a straight punch, a hook and an uppercut. You also have three defensive tools - a slip, a bob and a block.`}</p>
    <p>{`That's it.`}</p>
    <p>{`Your job is to combine all of these into a successful strategy that will ensure the other guy is in more pain than you are. All while making sure you can still move when you're done.`}</p>
    <p>{`Physically, boxing is the hardest thing you have ever done. Imagine having sex for an hour. Now imagine compressing all that activity into 20 minutes. That's the kind of intensity boxers endure.`}</p>
    <h2 {...{
      "id": "entrepreneurship"
    }}>{`Entrepreneurship`}</h2>
    <p>{`Make something people want.`}</p>
    <p>{`At your disposal are three simple attacking tools - selling, coding and marketing. You also have three defensive tools - metrics, talking to users and investors.`}</p>
    <p>{`Your job is to combine all of these into a successful strategy that will ensure you sell more stuff than your competitors. All while making sure there is enough money in the bank to pay for everyone's food.`}</p>
    <p>{`Mentally, entrepreneurship is the hardest thing you have ever done. `}<a parentName="p" {...{
        "href": "http://paulgraham.com",
        "title": "Paul Graham (computer programmer)"
      }}>{`Paul Graham`}</a>{` likened startup life to having one's whole career compacted into about three years of insane activity.`}</p>
    <h2 {...{
      "id": "what-boxing-can-teach-you"
    }}>{`What boxing can teach you`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/kaput.gif",
        "alt": "Kaput",
        "title": "Kaput"
      }}></img></p>
    <blockquote>
      <p parentName="blockquote">{`The nature of boxing, the nature of the sport, is never to quit.`}</p>
      <p parentName="blockquote">{`A man's fight to the death, short of the death preferrably. But sometimes to the death.`}</p>
      <p parentName="blockquote">{`On the other side, it's about you continuing on no matter how bad it looks. That's what the whole thing is about. ~ `}<a parentName="p" {...{
          "href": "http://en.wikipedia.org/wiki/Ferdie_Pacheco",
          "title": "Ferdie Pacheco"
        }}>{`Ferdie Pacheco`}</a>{`, Ali's ringside guy`}</p>
    </blockquote>
    <p>{`One of the defining moments of boxing, for me, is in the 13th round of `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Thrilla_in_Manila",
        "title": "Thrilla in Manila"
      }}>{`Thrilla in Manilla`}</a>{` - Ali and `}<a parentName="p" {...{
        "href": "http://www.joefrazier.com/",
        "title": "Joe Frazier"
      }}>{`Frazier`}</a>{`'s last fight - when Ali hits Frazier with such force his mouthpiece is shot out of his mouth into the fifth row of spectators.`}</p>
    <p>{`Without batting an eye, Frazier just punches back. Then continues without a mouthpiece for the last two minutes of the round. Then comes back for more in 14th round. This time practically blind.`}</p>
    <p>{`I'm not saying you need to go into a grueling 12 round professional boxing fight to learn about determination and forcing yourself through all obstacles - just your average boxing practice will suffice.`}</p>
    <p>{`A minute of working furiously on a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Punching_bag",
        "title": "Punching bag"
      }}>{`punching bag`}</a>{` will teach you plenty about determination and stick-to-itness.`}</p>
    <p>{`The first twenty seconds are joyful, glorious, the next ten seconds you start to get tired, by the 40th second you can barely move your arms, by the 50th second you are breathing like a locomotive and can't memorize a 5 punch sequence. Come second 60 and all you can think about is collapsing to the floor.`}</p>
    <p>{`You must do 20 pushups instead.`}</p>
    <p>{`Launching a product follows a similar pattern - first comes jubilous coding and hacking marathons, you could go without sleep for years and just punch out code like a madman! After a week you're tired, you need sleep and the code you produce is ugly - done on autopilot.`}</p>
    <p>{`Then you finish. The product is awesome! All you can think of is that warm bed of yours.`}</p>
    <p>{`Now you must market.`}</p>
    <h2 {...{
      "id": "immediate-boxing-benefits"
    }}>{`Immediate boxing benefits`}</h2>
    <p>{`Keeping fit and staying healthy is `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/why-you-dont-exercise-every-day/swizec/3456",
        "title": "Why you don’t exercise every day"
      }}>{`pretty damn important for everyone`}</a>{`, not just entrepreneurs. This is something we can all agree on in 2012 right?`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/savelife223.png",
        "alt": "Real man",
        "title": "Real man"
      }}></img></p>
    <p>{`Boxing is an incredibly effective `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Anaerobic_exercise",
        "title": "Anaerobic exercise"
      }}>{`anaerobic exercise`}</a>{`. Constantly flailing your arms around, dancing on your feet and moving your body around like that is said to burn about 900 calories an hour - that's a whole pizza!`}</p>
    <p>{`And it's fun!`}</p>
    <p>{`What I really love about boxing is actually how relaxing it is for the brain. In that hour and a half in a boxing gym the rest of the world doesn't exist - there's nothing but me, my gloves and that guy trying to rearrange my face.`}</p>
    <p>{`Whatever problems you have with the latest product, whatever fights you're in, any and all worries, they all vanish the moment you put on those gloves. There's no room for them anyway.`}</p>
    <h2 {...{
      "id": "punch-someone"
    }}>{`Punch someone`}</h2>
    <p>{`But the greatest thing boxing gives you is that you get to punch someone in the face!`}</p>
    <p>{`On the `}<a parentName="p" {...{
        "href": "http://www.hulu.com/battlestar-galactica",
        "title": "Battlestar Galactica"
      }}>{`Battlestar Galactica`}</a>{` the crew would spar in the ring to settle personal grudges. Startups could benefit from the same - any organization where tensions run high and there's a lot of pressure from otuside could.`}</p>
    <p>{`You love your cofounders, but sometimes you just want to punch them in the face, right?`}</p>
    <p><a parentName="p" {...{
        "href": "http://swizec.com/blog/wp-content/uploads/2012/02/2346288052_44b7311cf7.jpg"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2012/02/2346288052_44b7311cf7.jpg",
          "alt": "Battlestar Galactica Sparring",
          "title": "Battlestar Galactica Sparring"
        }}></img></a></p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.thegrio.com/sports/legendary-boxer-joe-frazier-dies-after-fight-with-cancer.php"
        }}>{`Legendary boxer Joe Frazier dies after fight with cancer`}</a>{` (thegrio.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.mysanantonio.com/news/article/Teenage-girl-from-Afghanistan-to-box-at-Olympics-3238198.php"
        }}>{`Teenage girl from Afghanistan to box at Olympics`}</a>{` (mysanantonio.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://newyork.cbslocal.com/2011/11/07/keidel-the-smoke-of-smokin%e2%80%99-joe-frazier/"
        }}>{`Keidel: The Smoke of Smokin' Joe Frazier`}</a>{` (newyork.cbslocal.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://bleacherreport.com/articles/930529-joe-frazier-muhammad-ali-why-historic-rivalry-will-never-be-matched"
        }}>{`You: Joe Frazier, Muhammad Ali: Why Historic Rivalry Will Never Be Matched`}</a>{` (bleacherreport.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=ef232933-4755-4561-a881-95b64118e709",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      